import { appstr } from "@coreapp/utils/appstr";
import { enumRulePairLogicCondition, QRulePair } from "@coreapp/CoreApp/QRulePair";
import { Alert, Button, Flex, Input } from "@theorgchartdev/storybook";
import { RSBFooter, RSBPrimaryButton, RSBSecondaryButton } from "./RSBFooter";
import { BlockCriteriaContainer } from "./BlockCriteriaContainer";
import type { SharedFilterDoc } from "../FilterPanel/useSharedFilterController";
import {
  enumChartRuleModifiers,
  type ChartRuleModifier,
  type RuleStatus
} from "./useChartRuleEditorController";
import { UIRuleError } from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";
import { ConfirmDeleteButton } from "./ConfirmDeleteButton";
import { chartModeTestId, planningTestId } from "./chartModeTestId";
import cx from "classnames";
import React, { useEffect, useMemo, useRef } from "react";

export type RulesMethods = {
  clearAllRules: () => void;
  addRule: (connectsWithCondition: string, ruleCondition: string, index?: number) => void;
  changeRule: (ruleIndex: number, rule: QRulePair) => void;
  removeRule: (ruleIndex: number) => void;
};

export type RulesEditorProps<T extends ChartRuleModifier = ChartRuleModifier> = {
  entity: enumChartRuleModifiers;
  editingElement: T;
  getRules: (element: T) => QRulePair[];
  sharedFilterDoc?: SharedFilterDoc;
  handleNameChangeValidation: (currentName: string, name: string) => any;
  elementInitialName: string;
  bannerDismissed: boolean;
  setBannerDismissed: (v: boolean) => void;
  savingShared: boolean;
  ruleEditStatus: RuleStatus;
  changeName: (name: string) => void;
  toggleRuleEditStatus: (rule: QRulePair) => void;
  insertRuleAt: (
    ruleIndex: number,
    rule: QRulePair,
    begginingOfOrBlock: boolean,
    endOfOrBlock: boolean
  ) => void;
  saveBlock: () => void;
  deleteSharedFilter: () => Promise<void>;
  isInEmptyState: () => boolean;
  ruleErrorsByUID: Record<number, UIRuleError>;
  nameErrorShown: boolean;
  methods: RulesMethods;
  extra?: React.ReactNode;
  handleGoBack: () => void;
  isNew: boolean;
  deleteAndGoBack?: () => void;
  startDrag?: () => void;
  endDrag?: () => void;
};

export const RulesEditor = <T extends ChartRuleModifier>({
  entity,
  editingElement,
  getRules,
  sharedFilterDoc,
  handleNameChangeValidation,
  elementInitialName,
  bannerDismissed,
  setBannerDismissed,
  savingShared,
  ruleEditStatus,
  changeName,
  toggleRuleEditStatus,
  insertRuleAt,
  saveBlock,
  deleteSharedFilter,
  isInEmptyState,
  ruleErrorsByUID,
  nameErrorShown,
  methods,
  handleGoBack,
  isNew,
  deleteAndGoBack,
  startDrag,
  endDrag,
  extra
}: RulesEditorProps<T>) => {
  const inputLabel = appstr("Entity name", { entity: appstr(entity) });
  const nameValidationObj = handleNameChangeValidation(
    elementInitialName,
    editingElement.GetName() ?? ""
  );
  const isFilter = entity === enumChartRuleModifiers.Filter;
  const modeTestId = (suffix: string) => chartModeTestId(isFilter ? "filter" : "highlight", suffix);
  const planningOverrideTestId = (suffix: string) =>
    planningTestId(isFilter ? "filter" : "highlight", suffix);

  const bannerKey =
    entity === enumChartRuleModifiers.Filter ? "Shared filter banner" : "Shared highlight banner";
  const saveMsgKey = (() => {
    if (isNew) {
      return isFilter ? "Create filter" : "Create highlight";
    }
    return "Save changes";
  })();
  const isOwner = sharedFilterDoc?.userRole === "owner";
  const hasActiveShares = (sharedFilterDoc?.sharedWith?.length ?? 0) > 0;

  const containerRef = useRef<HTMLDivElement>(null);
  const prevErrorUIDsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const errorUIDs = Object.keys(ruleErrorsByUID).map(Number);
    if (errorUIDs.length === 0) {
      prevErrorUIDsRef.current = new Set();
      return;
    }
    const isNewErrors = errorUIDs.some(uid => !prevErrorUIDsRef.current.has(uid));
    prevErrorUIDsRef.current = new Set(errorUIDs);
    if (!isNewErrors || !containerRef.current) return;
    const selector = errorUIDs.map(uid => `[data-rule-uid="${uid}"]`).join(", ");
    containerRef.current
      .querySelector<HTMLElement>(selector)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [ruleErrorsByUID]);

  const deleteButton = useMemo(() => {
    const isShared = sharedFilterDoc != null;

    let onDelete: (() => void) | undefined;
    let confirmMessage: string | undefined;

    if (isShared && isOwner) {
      onDelete = deleteSharedFilter;
      confirmMessage = hasActiveShares
        ? appstr(isFilter ? "Delete shared filter confirm" : "Delete shared highlight confirm")
        : appstr(isFilter ? "Delete filter confirm" : "Delete highlight confirm");
    } else if (!isShared && !isNew && deleteAndGoBack) {
      onDelete = deleteAndGoBack;
      confirmMessage = appstr(isFilter ? "Delete filter confirm" : "Delete highlight confirm");
    }

    if (!onDelete || !confirmMessage) return null;

    const isSharedOwnerDelete = isShared && isOwner;

    return (
      <ConfirmDeleteButton
        onSubmit={onDelete}
        tooltipMessage={appstr("Delete")}
        headerTitle={appstr(isFilter ? "Delete filter?" : "Delete highlight?")}
        triggerTestId={
          planningOverrideTestId(isSharedOwnerDelete ? "shared-delete-btn" : "local-delete-btn") ??
          (isSharedOwnerDelete ? "shared-filter-delete-btn" : "local-filter-delete-btn")
        }
      >
        {confirmMessage}
      </ConfirmDeleteButton>
    );
  }, [
    isNew,
    isOwner,
    hasActiveShares,
    sharedFilterDoc,
    isFilter,
    deleteAndGoBack,
    deleteSharedFilter
  ]);

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      <Flex direction="column" height={"100%"} className="ocn-filter-block-editor">
        {hasActiveShares && !bannerDismissed && (
          <Flex padding="16px 16px 0">
            <Alert
              severity="info"
              data-testid={modeTestId("shared-banner")}
              alertActions={{ closeAction: () => setBannerDismissed(true) }}
            >
              {appstr(bannerKey)}
            </Alert>
          </Flex>
        )}
        <Flex padding="16px" className="ocn-filter-group-name-input">
          <Input
            label={inputLabel}
            placeholder={inputLabel}
            value={editingElement.GetName() ?? ""}
            onInput={e => changeName((e.target as HTMLInputElement).value)}
            size="medium"
            require
            autoFocus
            dataTestId="filter-name-input"
            message={
              nameErrorShown && nameValidationObj.showText ? nameValidationObj.msg : undefined
            }
            status={nameErrorShown && nameValidationObj.showText ? "error" : undefined}
          />
        </Flex>
        <Flex
          direction="column"
          flex={"1"}
          overflow={"auto"}
          className="ocn-list-scrollbar"
          padding="0 16px 16px"
        >
          <Flex
            className="sidebar-header-buttons"
            justifyContent={extra ? "space-between" : "flex-end"}
            alignItems="center"
          >
            {extra}
            <Button
              modifier="secondary"
              size="small"
              iconProps={{
                name: "broom-wide"
              }}
              onClick={methods.clearAllRules}
              children={appstr("Clear all")}
              disabled={isInEmptyState()}
              dataTestId={modeTestId("clear-all-btn")}
            />
          </Flex>
          <BlockCriteriaContainer
            rules={getRules(editingElement)}
            ruleEditStatus={ruleEditStatus}
            toggleRuleEditStatus={toggleRuleEditStatus}
            handleRuleChange={methods.changeRule}
            handleRuleRemove={methods.removeRule}
            handleInsertRuleAt={insertRuleAt}
            handleInsertEmptyRuleAt={methods.addRule}
            testIdPrefix={modeTestId("criteria")}
            planningTestIdPrefix={planningOverrideTestId("criteria")}
            ruleErrorsByUID={ruleErrorsByUID}
            onDragStarted={startDrag}
            onDragEnded={endDrag}
          />
          <Flex direction="column">
            <div className={cx("sidebar-header-buttons")}>
              <Button
                modifier="secondary"
                size="small"
                iconProps={{
                  name: "plus-solid"
                }}
                onClick={() =>
                  methods.addRule(enumRulePairLogicCondition.and, enumRulePairLogicCondition.and)
                }
                children={appstr("Add criteria")}
                dataTestId={modeTestId("add-criteria-btn")}
              />
            </div>
          </Flex>
        </Flex>
        <RSBFooter leftSlot={deleteButton}>
          <RSBSecondaryButton
            onClick={() => handleGoBack()}
            dataTestId={modeTestId("cancel-btn")}
          />
          <RSBPrimaryButton
            onClick={saveBlock}
            disabled={savingShared}
            dataTestId={planningOverrideTestId("save-btn") ?? "rule-input-menu-Save"}
          >
            {appstr(saveMsgKey)}
          </RSBPrimaryButton>
        </RSBFooter>
      </Flex>
    </div>
  );
};
